<script setup lang="ts">
  import { Skill, SkillType, UserSkill } from '@borg/types';
  import { MpAutocomplete, MpSelect, MpTextArea, pushToastMessage } from '@borg/ui';
  import { getSkillLevels } from '@/layers/MyProfile/utils/skills';
  import { EditModalEvents } from './types';
  import { CreateUserSkillSchema } from '@borg/validation';

  const props = defineProps<{
    isOpen: boolean;
    type: SkillType;
    id?: string;
  }>();

  const emits = defineEmits<EditModalEvents>();

  const { t } = useI18n();
  const skillLevels = getSkillLevels();

  const TRANSLATION_KEYS =
    props.type === 'digital'
      ? {
          TITLE: t('MY_PROFILE_PAGE.DIGITAL_SKILLS_MODAL.TITLE'),
          SKILL_LABEL: t('MY_PROFILE_PAGE.DIGITAL_SKILLS_MODAL.SKILL_NAME_LABEL'),
          SKILL_LEVEL_LABEL: t('MY_PROFILE_PAGE.DIGITAL_SKILLS_MODAL.SKILL_LEVEL_LABEL'),
          SKILL_DESCRIPTION_LABEL: t('MY_PROFILE_PAGE.DIGITAL_SKILLS_MODAL.DESCRIPTION_LABEL'),
        }
      : {
          TITLE: t('MY_PROFILE_PAGE.OTHER_SKILLS_MODAL.TITLE'),
          SKILL_LABEL: t('MY_PROFILE_PAGE.OTHER_SKILLS_MODAL.SKILL_NAME_LABEL'),
          SKILL_LEVEL_LABEL: t('MY_PROFILE_PAGE.OTHER_SKILLS_MODAL.SKILL_LEVEL_LABEL'),
          SKILL_DESCRIPTION_LABEL: t('MY_PROFILE_PAGE.OTHER_SKILLS_MODAL.DESCRIPTION_LABEL'),
        };

  const { values, state, validate, resetStates, setValues } = useForm({
    initialData: () => transformEntity(),
    schema: CreateUserSkillSchema,
  });

  const submitInProgress = ref(false);
  const getInProgress = ref(false);
  const isAwaitingResponse = computed(() => submitInProgress.value || getInProgress.value);

  async function search(query: string): Promise<Skill[]> {
    const { data, status } = await skillSearchRepository.getMany(query, props.type);
    return status.value === 'success' ? data.value || [] : [];
  }

  function onSelectSkill(selected: Skill) {
    values.skill = selected;
  }

  function onUpdateSkillName(name: Skill['name']) {
    values.skill = { name };
  }

  function transformEntity(input?: UserSkill) {
    return {
      skill: input?.skill ?? { id: 0, name: '' },
      level: input?.level ? skillLevels.find((s) => s.value === input.level) : undefined,
      description: input?.description ?? '',
    };
  }

  async function onModalOpened() {
    if (props.id) {
      getInProgress.value = true;
      const { data: skill, status } = await skillsRepository.get(props.id);
      if (status.value === 'success' && skill.value) {
        setValues(transformEntity(skill.value));
        getInProgress.value = false;
      } else {
        pushToastMessage({
          type: 'alert',
          subtitle: t('MY_PROFILE_PAGE.FETCH_ERROR_TOAST.SUBTITLE'),
        });
        onClose();
      }
    }
  }

  async function onSubmit() {
    const valid = await validate();
    if (!valid) {
      return;
    }
    const _values = {
      ...values,
      level: values.level?.value || 'basic',
    };
    try {
      submitInProgress.value = true;
      props.id
        ? await skillsRepository.save({ ..._values, id: props.id })
        : await skillsRepository.create({ ..._values, type: props.type });
      submitInProgress.value = false;
      emits('change');
      onClose();
    } catch (e) {
      submitInProgress.value = false;
      pushToastMessage({
        type: 'alert',
        subtitle: t('MY_PROFILE_PAGE.SUBMIT_ERROR_TOAST.SUBTITLE'),
      });
    }
  }

  function onClose() {
    resetStates();
    emits('update:isOpen', false);
  }
</script>

<template>
  <SiteActionsModal
    :is-open="isOpen"
    :title="TRANSLATION_KEYS.TITLE"
    :in-progress="isAwaitingResponse"
    @open="onModalOpened"
    @confirm="onSubmit"
    @cancel="onClose"
  >
    <MyProfileEditModalForm>
      <MpAutocomplete
        :model-value="values.skill.name"
        :disabled="isAwaitingResponse"
        class="form__row-half"
        :search="search"
        :get-value="(o: Skill) => o.id"
        :get-label="(o: Skill) => o.name"
        :label="TRANSLATION_KEYS.SKILL_LABEL"
        :error="state.skill.message ? t(state.skill.message) : undefined"
        required
        :test="`${type}-skills-skill-input`"
        :autofocus="!values.skill.name"
        @update:model-value="onUpdateSkillName"
        @selected="onSelectSkill"
      />
      <MpSelect
        v-model="values.level"
        :disabled="isAwaitingResponse"
        class="form__row-half"
        :options="skillLevels"
        :data-test="`${type}-skills-level-select`"
        :label="TRANSLATION_KEYS.SKILL_LEVEL_LABEL"
        :error="state.level.message ? t(state.level.message) : undefined"
        :unselectable="false"
        required
      />
      <div class="form__row-full">
        <MpTextArea
          v-model="values.description"
          :disabled="isAwaitingResponse"
          :label="TRANSLATION_KEYS.SKILL_DESCRIPTION_LABEL"
          :error="state.description.message"
          rows="6"
        />
      </div>
    </MyProfileEditModalForm>
  </SiteActionsModal>
</template>
